import React, { InputHTMLAttributes, useState } from 'react';
import ReactInputMask from 'react-input-mask';
import { FieldErrors, RegisterOptions, UseFormRegister } from 'react-hook-form';
import eye from '../../assets/sprites/eye-fill.svg';
import eyeSlash from '../../assets/sprites/eye-slash-fill.svg';
import deleteIcon from '../../assets/sprites/Verctor.svg';

const getErrorMessage = (
  errors: FieldErrors | string,
  inputName: string,
  index: number
): string | null => {
  if (typeof errors === 'string') {
    return errors;
  }
  const keys = inputName.split('.');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let currentError: any = errors;

  for (const key of keys) {
    if (Array.isArray(currentError)) {
      currentError = currentError[index];
    }
    if (currentError && currentError[key]) {
      currentError = currentError[key];
    } else {
      return null;
    }
  }

  return currentError.message;
};

interface IInputField extends Partial<InputHTMLAttributes<HTMLInputElement>> {
  id?: string;
  inputName?: string;
  label?: string;
  type?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors?: FieldErrors<any> | string;
  as?: React.ElementType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register?: UseFormRegister<any>;
  validationSchema?: RegisterOptions;
  index?: number;
  defaultValue?: string;
  placeholder?: string;
  mask?: string | null;
  className?: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  hasMarginAndPadding?: boolean;
  deleteIconHandler?: (e: React.MouseEvent) => void;
  // Произвольная нода справа внутри input'а (иконка, кнопка-скрепка
  // и т.п.). Кладётся в тот же inner-relative-контейнер, что и
  // встроенные eye/delete — позиционируется через top-1/2 относительно
  // самого input, а не относительно блока «label + input», поэтому
  // вертикальный центр совпадает с центром поля ввода. Вызывающий
  // должен сам поставить input'у className="pr-10" (или больше),
  // чтобы текст не залезал под слот.
  rightSlot?: React.ReactNode;
}

const InputField = React.forwardRef(
  (
    {
      id = '',
      inputName = '',
      label = '',
      type = 'text',
      errors = {},
      register,
      validationSchema = {},
      index = 0,
      defaultValue,
      mask = null,
      as = 'input',
      deleteIconHandler,
      onBlur,
      onChange,
      value,
      name,
      className = '',
      placeholder = '',
      hasMarginAndPadding = true,
      rightSlot,
      ...props
    }: IInputField,
    ref
  ) => {
    const [show, setShow] = useState(false);
    const handleClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setShow(!show);
    };

    const errorMessage = getErrorMessage(errors, inputName, index);
    const isRequired = validationSchema?.required;

    // Если value/onChange пришли сверху (например, через <Controller /> из
    // react-hook-form), считаем поле контролируемым и НЕ вызываем register —
    // иначе поле окажется зарегистрировано дважды, а onChange из props
    // будет перетёрт onChange от register, из-за чего, в частности,
    // масксированный инпут телефона не сохраняет значение в форму.
    const isControlled = onChange !== undefined || value !== undefined;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const registered: any =
      !isControlled && typeof register === 'function'
        ? register(inputName, validationSchema)
        : null;

    const Component = (mask ? ReactInputMask : (as as React.ElementType)) as React.ElementType;

    const inputBaseClass =
      'font-montserrat w-full px-3 h-[40px] border border-[#D0D3D8] rounded-[10px] text-[14px] font-normal transition-all duration-200 focus:outline-none focus:border-[#007aff] focus:shadow-[0_0_0_4px_rgba(0,122,255,0.21)] hover:border-[#007aff] hover:shadow-[0_0_0_4px_rgba(0,122,255,0.21)]';
    const inputErrorClass =
      'border-[#E3282A] focus:border-[#E3282A] focus:shadow-[0_0_0_4px_rgba(239,44,44,0.21)] hover:border-[#E3282A] hover:shadow-[0_0_0_4px_rgba(239,44,44,0.21)]';

    const combinedRef = (el: HTMLInputElement | null) => {
      if (registered && typeof registered.ref === 'function') registered.ref(el);
      if (typeof ref === 'function') ref(el);
      else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (registered && typeof registered.onBlur === 'function') registered.onBlur(e);
      if (typeof onBlur === 'function') onBlur(e);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const bindings: Record<string, any> = isControlled
      ? {
          name: (name as string) ?? inputName,
          value: value ?? '',
          onChange,
        }
      : {
          name: registered?.name ?? inputName,
          onChange: registered?.onChange,
        };

    return (
      <div className={`relative pb-[10px] ${hasMarginAndPadding ? 'my-4' : ''}`}>
        {/* Без label пустой <label> как flex-элемент всё равно получал mb-2:
            у телефонов в регистрации отступ подпись→поле был 16px вместо 8 (35173). */}
        {(label || errorMessage) && (
          <div className={`flex justify-between ${!label ? 'block' : ''}`}>
            <label
              htmlFor={id}
              className="mb-2 font-montserrat text-[#2d2c2c] text-[16px] font-medium"
            >
              {label}
              {isRequired && '*'}
            </label>
            {errorMessage && (
              <p className="text-[#E3282A] text-[14px] font-medium font-montserrat text-right mb-2">
                {errorMessage}
              </p>
            )}
          </div>
        )}
        <div className="relative">
          <Component
            {...props}
            {...(mask ? { mask } : {})}
            id={id}
            type={show ? 'text' : type}
            {...(defaultValue !== undefined && !isControlled
              ? { defaultValue }
              : {})}
            className={`${inputBaseClass} ${errorMessage ? inputErrorClass : ''} ${className}`}
            {...bindings}
            ref={combinedRef}
            onBlur={handleBlur}
            placeholder={placeholder}
          />
          {type === 'password' && (
            <button
              type="button"
              onClick={handleClick}
              className="cursor-pointer border-none bg-none w-[25px] absolute right-[5px] top-1/2 -translate-y-1/2 z-[99] hover:brightness-0 hover:saturate-100 hover:sepia hover:hue-rotate-[330deg]"
            >
              <img src={show ? eye : eyeSlash} alt=" " />
            </button>
          )}
          {deleteIconHandler && (
            <button
              type="button"
              onClick={deleteIconHandler}
              className="cursor-pointer border-none bg-none w-[25px] absolute right-[5px] top-1/2 -translate-y-1/2 z-[99]"
            >
              <img src={deleteIcon} alt=" " />
            </button>
          )}
          {rightSlot && (
            // Тот же позиционирующий каркас, что у eye/delete выше —
            // вертикальный центр относительно input'а, не относительно
            // внешнего блока с label.
            <div className="absolute right-3 top-1/2 -translate-y-1/2 z-[99]">
              {rightSlot}
            </div>
          )}
        </div>
      </div>
    );
  }
);

export default InputField;
